import type { UserCapabilities } from "../services/es";

export type NavGroup = "Data" | "Workspace" | "Security" | "System" | "Help" | "Settings";

export interface PagePathConfig {
  path: string;
  requiresConnection: boolean;
  showTimeControls: boolean;
  /** Key of `UserCapabilities` that must be `true` for the page to appear in the sidebar. */
  requiredCapability?: keyof UserCapabilities;
  /** ContentSkeleton variant shown while the lazy page chunk loads. */
  skeletonVariant?: "table" | "cards" | "chart" | "list" | "detail-panel" | "linear";
  nav: {
    label: string;
    group: NavGroup;
    order: number;
    showInSidebar: boolean;
    iconKey?: string;
  };
}

export const PAGE_PATHS = {
  dashboards: {
    path: "/dashboards",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "cards",
    nav: {
      label: "Dashboards",
      group: "Workspace",
      order: 10,
      showInSidebar: true,
      iconKey: "DashboardIcon",
    },
  },
  discover: {
    path: "/discover",
    requiresConnection: true,
    showTimeControls: true,
    skeletonVariant: "table",
    nav: {
      label: "Query Lab",
      group: "Data",
      order: 20,
      showInSidebar: true,
      iconKey: "SearchIcon",
    },
  },
  logs: {
    path: "/logs",
    requiresConnection: true,
    showTimeControls: true,
    skeletonVariant: "list",
    nav: {
      label: "Logs",
      group: "Data",
      order: 30,
      showInSidebar: true,
      iconKey: "SubjectIcon",
    },
  },
  logsExplorer: {
    path: "/logs-explorer",
    requiresConnection: true,
    showTimeControls: true,
    skeletonVariant: "table",
    nav: {
      label: "Logs Explorer",
      group: "Data",
      order: 31,
      showInSidebar: false,
      iconKey: "SubjectIcon",
    },
  },
  explore: {
    path: "/explore",
    requiresConnection: true,
    showTimeControls: true,
    skeletonVariant: "chart",
    nav: {
      label: "Metrics",
      group: "Data",
      order: 40,
      showInSidebar: true,
      iconKey: "ExploreIcon",
    },
  },
  traces: {
    path: "/traces",
    requiresConnection: true,
    showTimeControls: true,
    skeletonVariant: "table",
    nav: {
      label: "Traces",
      group: "Data",
      order: 50,
      showInSidebar: true,
      iconKey: "TimelineIcon",
    },
  },
  profiling: {
    path: "/profiling",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "chart",
    nav: {
      label: "Profiling",
      group: "Data",
      order: 60,
      showInSidebar: true,
      iconKey: "SpeedIcon",
    },
  },
  profilingAdvanced: {
    path: "/profiling/advanced",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "chart",
    nav: {
      label: "Profiling (Advanced)",
      group: "Data",
      order: 61,
      showInSidebar: false,
      iconKey: "SpeedIcon",
    },
  },
  services: {
    path: "/services",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "table",
    nav: {
      label: "Services",
      group: "Workspace",
      order: 20,
      showInSidebar: true,
      iconKey: "MiscellaneousServicesIcon",
    },
  },
  serviceDashboard: {
    path: "/services/:serviceName",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "detail-panel",
    nav: {
      label: "Service Dashboard",
      group: "Workspace",
      order: 21,
      showInSidebar: false,
      iconKey: "MiscellaneousServicesIcon",
    },
  },
  kubernetes: {
    path: "/kubernetes",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "table",
    nav: {
      label: "Kubernetes",
      group: "Workspace",
      order: 30,
      showInSidebar: true,
      iconKey: "CloudIcon",
    },
  },
  kubernetesCluster: {
    path: "/kubernetes/cluster/:clusterName",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "detail-panel",
    nav: {
      label: "Kubernetes Cluster",
      group: "Workspace",
      order: 31,
      showInSidebar: false,
      iconKey: "CloudIcon",
    },
  },
  kubernetesNamespace: {
    path: "/kubernetes/namespace/:namespace",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "detail-panel",
    nav: {
      label: "Kubernetes Namespace",
      group: "Workspace",
      order: 32,
      showInSidebar: false,
      iconKey: "CloudIcon",
    },
  },
  kubernetesWorkload: {
    path: "/kubernetes/workload/:kind/:name",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "detail-panel",
    nav: {
      label: "Kubernetes Workload",
      group: "Workspace",
      order: 33,
      showInSidebar: false,
      iconKey: "CloudIcon",
    },
  },
  kubernetesPod: {
    path: "/kubernetes/pod/:podName",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "detail-panel",
    nav: {
      label: "Kubernetes Pod",
      group: "Workspace",
      order: 34,
      showInSidebar: false,
      iconKey: "CloudIcon",
    },
  },
  hosts: {
    path: "/hosts",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "table",
    nav: {
      label: "Hosts",
      group: "Workspace",
      order: 40,
      showInSidebar: true,
      iconKey: "DnsIcon",
    },
  },
  hostsLinux: {
    path: "/hosts/linux",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "table",
    nav: {
      label: "Linux Hosts",
      group: "Workspace",
      order: 41,
      showInSidebar: false,
      iconKey: "DnsIcon",
    },
  },
  hostsWindows: {
    path: "/hosts/windows",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "table",
    nav: {
      label: "Windows Hosts",
      group: "Workspace",
      order: 42,
      showInSidebar: false,
      iconKey: "DnsIcon",
    },
  },
  hostsMacos: {
    path: "/hosts/macos",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "table",
    nav: {
      label: "macOS Hosts",
      group: "Workspace",
      order: 43,
      showInSidebar: false,
      iconKey: "DnsIcon",
    },
  },
  hostDetail: {
    path: "/hosts/:hostId",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "detail-panel",
    nav: {
      label: "Host Detail",
      group: "Workspace",
      order: 44,
      showInSidebar: false,
      iconKey: "DnsIcon",
    },
  },
  console: {
    path: "/console",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "linear",
    nav: {
      label: "Console",
      group: "System",
      order: 50,
      showInSidebar: true,
      iconKey: "TerminalIcon",
    },
  },
  chat: {
    path: "/chat",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "linear",
    nav: {
      label: "Chat",
      group: "Workspace",
      order: 60,
      showInSidebar: false,
      iconKey: "ChatIcon",
    },
  },
  clusterOverview: {
    path: "/cluster-overview",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "cards",
    nav: {
      label: "Overview",
      group: "System",
      order: 10,
      showInSidebar: true,
      iconKey: "InfoIcon",
    },
  },
  clusterHealth: {
    path: "/cluster-health",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "cards",
    nav: {
      label: "Health",
      group: "System",
      order: 11,
      showInSidebar: true,
      iconKey: "HealthAndSafetyIcon",
    },
  },

  clusterTasks: {
    path: "/cluster-tasks",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "table",
    nav: {
      label: "Tasks",
      group: "System",
      order: 18,
      showInSidebar: true,
      iconKey: "PendingActionsIcon",
    },
  },
  clusterCapacity: {
    path: "/cluster-capacity",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "cards",
    nav: {
      label: "Capacity",
      group: "System",
      order: 13,
      showInSidebar: false,
      iconKey: "MemoryIcon",
    },
  },
  clusterShards: {
    path: "/cluster-shards",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "table",
    nav: {
      label: "Shards",
      group: "System",
      order: 14,
      showInSidebar: false,
      iconKey: "ViewModuleIcon",
    },
  },
  clusterResilience: {
    path: "/cluster-resilience",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "table",
    nav: {
      label: "Resilience",
      group: "System",
      order: 17,
      showInSidebar: false,
      iconKey: "ShieldIcon",
    },
  },
  addData: {
    path: "/add-data",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "list",
    nav: {
      label: "Add Data",
      group: "Data",
      order: 10,
      showInSidebar: true,
      iconKey: "RocketLaunchIcon",
    },
  },
  packageBuilder: {
    path: "/package-builder",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "list",
    nav: {
      label: "Package Editor",
      group: "System",
      order: 17,
      showInSidebar: false,
    },
  },
  dataStreams: {
    path: "/data-streams",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "table",
    nav: {
      label: "Data Streams",
      group: "System",
      order: 23,
      showInSidebar: true,
      iconKey: "DatasetIcon",
    },
  },
  nodes: {
    path: "/nodes",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "table",
    nav: {
      label: "Nodes",
      group: "System",
      order: 20,
      showInSidebar: true,
      iconKey: "MemoryIcon",
    },
  },
  nodeDetail: {
    path: "/nodes/:nodeId",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "detail-panel",
    nav: {
      label: "Node detail",
      group: "System",
      order: 24,
      showInSidebar: false,
      iconKey: "MemoryIcon",
    },
  },
  indices: {
    path: "/indices",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "table",
    nav: {
      label: "Indices",
      group: "System",
      order: 25,
      showInSidebar: true,
      iconKey: "StorageIcon",
    },
  },
  storageExplorer: {
    path: "/storage-explorer",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "table",
    nav: {
      label: "Storage Explorer",
      group: "System",
      order: 27,
      showInSidebar: true,
      iconKey: "StorageIcon",
    },
  },
  ingestPipelines: {
    path: "/ingest-pipelines",
    requiresConnection: true,
    requiredCapability: "canReadIngestPipelines",
    showTimeControls: false,
    skeletonVariant: "table",
    nav: {
      label: "Ingest Pipelines",
      group: "System",
      order: 26,
      showInSidebar: true,
      iconKey: "AccountTreeIcon",
    },
  },
  clusterSettings: {
    path: "/cluster-settings",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "table",
    nav: {
      label: "Cluster Settings",
      group: "System",
      order: 21,
      showInSidebar: true,
      iconKey: "SettingsIcon",
    },
  },
  nodesHotThreads: {
    path: "/nodes-hot-threads",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "table",
    nav: {
      label: "Hot Threads",
      group: "System",
      order: 28,
      showInSidebar: true,
      iconKey: "SpeedIcon",
    },
  },
  watcherGetWatch: {
    path: "/watcher-get-watch",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "detail-panel",
    nav: {
      label: "Watchers",
      group: "System",
      order: 29,
      showInSidebar: true,
      iconKey: "PendingActionsIcon",
    },
  },
  ilm: {
    path: "/ilm",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "table",
    nav: {
      label: "Index Lifecycle Management",
      group: "System",
      order: 31,
      showInSidebar: true,
      iconKey: "PolicyIcon",
    },
  },
  transforms: {
    path: "/transforms",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "table",
    nav: {
      label: "Transforms",
      group: "System",
      order: 32,
      showInSidebar: true,
      iconKey: "TransformIcon",
    },
  },
  snapshots: {
    path: "/snapshots",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "table",
    nav: {
      label: "Snapshots",
      group: "System",
      order: 33,
      showInSidebar: true,
      iconKey: "BackupIcon",
    },
  },
  templates: {
    path: "/templates",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "table",
    nav: {
      label: "Index Templates",
      group: "System",
      order: 22,
      showInSidebar: true,
      iconKey: "DescriptionIcon",
    },
  },
  fleet: {
    path: "/fleet",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "cards",
    nav: {
      label: "Fleet",
      group: "System",
      order: 15,
      showInSidebar: true,
      iconKey: "SecurityIcon",
    },
  },
  fleetAgentDetail: {
    path: "/fleet/agents/:agentId",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "detail-panel",
    nav: {
      label: "Fleet Agent Detail",
      group: "System",
      order: 16,
      showInSidebar: false,
      iconKey: "SecurityIcon",
    },
  },
  investigate: {
    path: "/investigate",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "table",
    nav: {
      label: "Investigate",
      group: "Security",
      order: 10,
      showInSidebar: true,
      iconKey: "PolicyIcon",
    },
  },
  users: {
    path: "/users",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "table",
    requiredCapability: "canReadSecurityUsers",
    nav: {
      label: "Users",
      group: "System",
      order: 30,
      showInSidebar: true,
      iconKey: "PeopleIcon",
    },
  },
  apiKeys: {
    path: "/api-keys",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "table",
    requiredCapability: "canReadApiKeys",
    nav: {
      label: "API Keys",
      group: "System",
      order: 35,
      showInSidebar: true,
      iconKey: "VpnKeyIcon",
    },
  },
  roles: {
    path: "/roles",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "table",
    requiredCapability: "canReadSecurityRoles",
    nav: {
      label: "Roles",
      group: "System",
      order: 40,
      showInSidebar: true,
      iconKey: "AdminPanelSettingsIcon",
    },
  },
  docs: {
    path: "/docs",
    requiresConnection: false,
    showTimeControls: false,
    skeletonVariant: "linear",
    nav: {
      label: "Docs",
      group: "Help",
      order: 10,
      showInSidebar: true,
      iconKey: "MenuBookIcon",
    },
  },
  settings: {
    path: "/settings",
    requiresConnection: true,
    showTimeControls: false,
    skeletonVariant: "linear",
    nav: {
      label: "LLM Settings",
      group: "Settings",
      order: 10,
      showInSidebar: false,
      iconKey: "SettingsIcon",
    },
  },
} as const satisfies Record<string, PagePathConfig>;

export type PageId = keyof typeof PAGE_PATHS;

/** Returns true when we positively know the user lacks a required capability. */
export function isHiddenByCapability(
  requiredCapability: keyof UserCapabilities | undefined,
  capabilities: UserCapabilities | null,
): boolean {
  if (!requiredCapability) return false;
  if (!capabilities) return false; // not yet fetched — keep visible
  return !capabilities[requiredCapability];
}

/** Sidebar section display order. Sections not listed here won't appear. */
export const NAV_SECTION_ORDER: NavGroup[] = [
  "Data",
  "Workspace",
  "Security",
  "System",
  "Help",
  "Settings",
];
